import { useCallback, useMemo } from 'react';

import { isEmpty } from 'lodash';

import { SafeHtml } from '@hsp/react-component-library';
import { isDefined, isNotDefined, parseJSON } from '@hsp/utilities';

import SecretKeyApproval from '@c/notifications/components/secret-key-approval/secret-key-approval.component';
import SecretKeyRequest from '@c/notifications/components/secret-key-request/secret-key-request.component';
import { ISecretKeyApprove, ISecretKeyRequest, NotificationTarget, SignalRNotification } from '@c/notifications/types';

import { INotificationItemProps } from 'types/components/notifications';

export const useNotificationItemConfiguration = ({
  notification: { data, id },
  onMarkAsRead,
  onRemove,
}: Pick<INotificationItemProps, 'notification' | 'onMarkAsRead' | 'onRemove'>) => {
  const handleMarkAsRead = useCallback(() => onMarkAsRead(id), [id, onMarkAsRead]);
  const handleRemove = useCallback(() => onRemove(id), [id, onRemove]);

  const defaultNotification = useCallback((value?: string) => {
    if (isNotDefined(value)) {
      return null;
    }

    const notification = parseJSON<SignalRNotification>(value);

    return isDefined(notification) && isDefined(notification.message) ? (
      <SafeHtml html={notification.message} />
    ) : (
      value
    );
  }, []);

  const message = useMemo(() => {
    const content = data?.content;
    const target = data?.target;

    if (isNotDefined(content) || isEmpty(content)) {
      return null;
    }

    if (typeof content === 'string') {
      if (isDefined(target)) {
        switch (target) {
          case NotificationTarget.ExportForUser:

          case NotificationTarget.QuantityForUser:

          case NotificationTarget.SecretKeyDeny:

          case NotificationTarget.Snapshot:
            return defaultNotification(content);

          case NotificationTarget.SecretKeyApprove: {
            const parsed = parseJSON<SignalRNotification<ISecretKeyApprove>>(content);

            if (
              isNotDefined(parsed) ||
              parsed.isError ||
              isNotDefined(parsed.message) ||
              isNotDefined(parsed.payload)
            ) {
              return null;
            }

            return <SecretKeyApproval {...{ ...parsed.payload, message: parsed.message }} />;
          }

          case NotificationTarget.SecretKeyRequest: {
            const parsed = parseJSON<SignalRNotification<ISecretKeyRequest>>(content);

            if (
              isNotDefined(parsed) ||
              parsed.isError ||
              isNotDefined(parsed.message) ||
              isNotDefined(parsed.payload)
            ) {
              return null;
            }

            return (
              <SecretKeyRequest {...{ ...parsed.payload, message: parsed.message, notificationId: id.toString() }} />
            );
          }
        }
      } else {
        return defaultNotification(content);
      }
    }

    return content;
  }, [data?.content, data?.target, defaultNotification, id]);

  return { handleMarkAsRead, handleRemove, message };
};
